import {
  metrics as metricConstants,
  scan as scanConstants,
  job as jobConstants,
  unicode
} from './../helpers';

export const getRelativeMetricsFunc = (
  baselineBuildType,
  metricsJobs,
  baselineJobsByImage,
  paramsByJobIds,
  scansByJobId,
  metricsByJobIds,
  highlightedImage,
  jobRelationshipsByParentId
) => {
  const latestDates = {};
  const relativeMetrics = [];
  for (let i = 0; i < metricsJobs.length; i++) {
    const job = metricsJobs[i];
    const image = jobConstants.getImageFileName(job, paramsByJobIds);
    if (!image) {
      continue;
    }

    const jobRelationships = jobRelationshipsByParentId &&
      jobRelationshipsByParentId[job.id];

    let jobMetrics = metricsByJobIds[job.id];
    if (!jobMetrics) {
      return [];
    }

    if (Array.isArray(jobRelationships)) {
      jobRelationships.forEach(jr => {
        if (metricsByJobIds[jr.child_job_id]) {
          jobMetrics = Object.assign(metricsByJobIds[jr.child_job_id], jobMetrics);
        }
      });
    }

    const isExpected = baselineBuildType === 'Expected';
    const getMetricsByType = (isCcf) => getRelativeMetricsByType(
      isCcf,
      image,
      job,
      jobMetrics,
      baselineJobsByImage,
      metricsByJobIds,
      isExpected,
      scansByJobId
    );
    const { metrics: ncfMetrics, ...info } = getMetricsByType(false);
    const ccfMetrics = getMetricsByType(true);

    if (!Object.hasOwnProperty.call(latestDates, image)) {
      latestDates[image] = info.createdDate;
    } else {
      if (latestDates[image] < info.createdDate) {
        latestDates[image] = info.createdDate;
      }
    }

    relativeMetrics.push({
      ...info,
      image,
      job: jobConstants.getJobNameNumber(job),
      jobId: job.id,
      highlight: image === highlightedImage,
      ncf: ncfMetrics,
      ccf: ccfMetrics.metrics
    });
  }

  for (let i = 0; i < relativeMetrics.length; i++) {
    const metric = relativeMetrics[i];
    metric.latest = metric.createdDate === latestDates[metric.image];
  }

  return relativeMetrics;
};

export const getRelativeMetricsByType = (
  isCcf,
  image,
  job,
  jobMetrics,
  baselineJobsByImage,
  metricsByJobIds,
  isExpected,
  scansByJobId
) => {
  const parsedJobMetrics = parseJobMetrics(jobMetrics, isCcf);
  const parsedBaselineMetrics = parseBaselineMetrics(
    image,
    baselineJobsByImage,
    metricsByJobIds,
    isExpected,
    isCcf,
    job.id,
    scansByJobId
  );
  const metricDiffs = getMetricDiffs(
    parsedJobMetrics,
    parsedBaselineMetrics,
    isExpected
  );

  const metricDate = new Date(job.created_at);
  const baselineMetricDate = isExpected ?
    parsedBaselineMetrics.scan ?
      new Date(parsedBaselineMetrics.scan.updatedDate) :
      undefined :
    new Date(parsedBaselineMetrics.job && parsedBaselineMetrics.job.created_at);

  const result = {
    scanKey: parsedBaselineMetrics.scanKey,
    createdDate: metricDate,
    baselineCreatedDate: baselineMetricDate,
    metrics: {
      durationMetrics: {
        current: parsedJobMetrics.duration,
        baseline: parsedBaselineMetrics.duration,
        percent: metricDiffs.durationPercent,
        diff: metricDiffs.durationDiff
      },
      hitCountMetrics: {
        current: parsedJobMetrics.hitCount,
        baseline: parsedBaselineMetrics.hitCount,
        diff: metricDiffs.hitCountDiff,
        diffResult: parsedJobMetrics.hitCountDiffResult,
        [metricConstants.NCF_MCAT_DIFFRESULT]: parsedJobMetrics.mcatDiffResult,
      },
      concerning: metricDiffs.concerning,
      artHitCounts: {
        current: parsedJobMetrics.artHitCounts,
        baseline: parsedBaselineMetrics.artHitCounts
      },
      sourceCountMetrics : {
        current: parsedJobMetrics.sourceCounts,
        baseline: parsedBaselineMetrics.sourceCounts,
        diff: metricDiffs.sourceCountDiff,
        diffResult: parsedJobMetrics.sourceCountDiffResult
      },
      sourceAttributeCountMetrics: {
        current: parsedJobMetrics.sourceAttributeCounts,
        baseline: parsedBaselineMetrics.sourceAttributeCounts,
        diff: metricDiffs.sourceAttributeCountDiff
      }
    }
  };

  return result;
};

const parseBaselineMetrics = (
  image,
  baselineJobsByImage,
  metricsByJobIds,
  isExpected,
  isCcf,
  jobId,
  scansByJobId
) => {
  const job = baselineJobsByImage[image];

  if (isExpected) {
    // pull baseline metrics from the SCAN ticket
    const scan = scansByJobId[jobId];
    const scanKey = scan ? scan.key : '';

    const scanBaselineMetrics = {
      job,
      scan,
      scanKey,
      duration: scanConstants.getTypeAverageDuration(scan, isCcf),
      artHitCounts: scanConstants.getTypedHitCounts(scan, isCcf),
      hitCount: scanConstants.getScanHitCount(scan, isCcf),
      sourceCounts: scanConstants.getTypedSourceCounts(scan, isCcf),
      sourceAttributeCounts: scanConstants.getTypedSourceAttributeCounts(scan, isCcf)
    };
    return scanBaselineMetrics;
  }
  else {
    if (!job) { return {}; }
    const baselineMetrics = job && metricsByJobIds[job.id];
    const jobMetrics = parseJobMetrics(baselineMetrics, isCcf);
    const jobBaselineMetrics = {
      job,
      scanKey: '',
      duration: jobMetrics.duration,
      hitCount: jobMetrics.hitCount,
      artHitCounts: jobMetrics.artHitCounts,
      sourceCounts: jobMetrics.sourceCounts,
      sourceAttributeCounts: jobMetrics.sourceAttributeCounts
    };
    return jobBaselineMetrics;
  }
};

export const getMetricDiffs = (
  parsedJobMetrics,
  parsedBaselineMetrics,
  isExpected
) => {
  let durationDiff, durationPercent;
  const hitCountDiff = parseInt(getRelativeValue(
    parsedJobMetrics.hitCount,
    parsedBaselineMetrics.hitCount
  ).diff);

  const sourceCountDiff = parseInt(getRelativeValue(
    parsedJobMetrics.sourceCounts,
    parsedBaselineMetrics.sourceCounts
  ).diff);

  const sourceAttributeCountDiff = parseInt(getRelativeValue(
    parsedJobMetrics.sourceAttributeCounts,
    parsedBaselineMetrics.sourceAttributeCounts
  ).diff);

  if (parsedBaselineMetrics.duration) {
    const relVal =
      getRelativeValue(parsedJobMetrics.duration, parsedBaselineMetrics.duration);
    durationDiff = relVal.diff;
    durationPercent = relVal.percent;
  }

  const percentVal = parseInt(durationPercent);

  let concerning = false;

  // if either of the diffs failed, then should be concerning
  if (parsedJobMetrics.hitCountDiffResult === 'fail' ||
    parsedJobMetrics.sourceCountDiffResult === 'fail') {
    concerning = true;
  }

  // not concerning if Expected build type and percent val is not a number because no
  // duration data for Expected right now
  if ((percentVal > 0 || isNaN(percentVal)) && !isExpected) {
    // It's only concerning if it's long running
    concerning = parsedJobMetrics.duration > 30;
  }

  if (hitCountDiff !== 0 && !isNaN(hitCountDiff)) {
    concerning = true;
  }

  if (sourceCountDiff !== 0 && !isNaN(sourceCountDiff)) {
    concerning = true;
  }

  if (sourceAttributeCountDiff !== 0 && !isNaN(sourceAttributeCountDiff)) {
    concerning = true;
  }

  if (durationPercent === +Infinity) {
    durationPercent = `+${unicode.INFINITY}`;
  }
  else if (durationPercent === -Infinity) {
    durationPercent = `-${unicode.INFINITY}`;
  }

  return {
    concerning,
    durationDiff,
    durationPercent,
    hitCountDiff,
    sourceCountDiff,
    sourceAttributeCountDiff
  };
};

const getRelativeValue = (jobValue, baselineValue) => {
  if (jobValue === undefined || baselineValue === undefined) { return {}; }

  const diff = jobValue - baselineValue;
  let percent;

  if (diff === 0) {
    percent = 0;
  } else if (baselineValue !== 0) {
    percent = Math.round(100 * diff / baselineValue);
  } else if (jobValue > 0) {
    percent = +Infinity;
  } else {
    percent = -Infinity;
  }

  return {
    diff,
    percent
  };
};

/**
 * Returns an object containing values to display on the BuildsPage table
 * @param {obj} jobMetrics - a single object containing a job's metrics
 * @param {*} isCcf - bool indicating whether to pull CCF values or NCF
 * @returns Object containing only the desired metrics to display
 */
const parseJobMetrics = (jobMetrics, isCcf) => {
  const result = {
    artHitCounts: metricConstants.getArtifactHitCounts(jobMetrics, isCcf),
    duration: metricConstants.getSearchDuration(jobMetrics, isCcf) +
      metricConstants.getDecryptionDuration(jobMetrics, isCcf),
    hitCount: metricConstants.getHitCount(jobMetrics, isCcf),
    hitCountDiffResult: metricConstants.getDiffResult('ARTIFACTS', jobMetrics, isCcf),
    mcatDiffResult: metricConstants.getMcatDiffResult(jobMetrics, isCcf),
    sourceAttributeCounts: metricConstants.getSourceAttributeCounts(jobMetrics, isCcf),
    sourceCountDiffResult: metricConstants.getDiffResult('SOURCES', jobMetrics, isCcf),
    sourceCounts: metricConstants.getSourceCounts(jobMetrics, isCcf),
  };
  return result;
};


// WEBPACK FOOTER //
// src/helpers/reducers.js